import type { DateOrRange } from '@/infrastructure/departure-date/store'
import type { DepartureDateFilterOptions } from '@/scopes/search/components/DepartureDateFilter/types'
import { mqMin, space, tok, tokPx, zIndex } from '@sunwebgroup/ui-components'
import { Button, IndeterminateProgressCircle } from '@sunwebgroup/ui-components/uds'
import { styled } from 'styled-components'
import { useDepartureDateStore } from '@/infrastructure/departure-date/useDepartureDateStore'
import { useIntl } from '@/intl'
import { DatesCalendar, MonthCalendar } from '@/scopes/search/components/DepartureDateFilter/Calendars'
import { useDepartureDateFilter } from '@/scopes/search/components/DepartureDateFilter/hooks'
import Tabs from '@/scopes/search/components/DepartureDateFilter/Tabs'
import { Toggle } from '@/scopes/search/components/DepartureDateFilter/Toggle'
import { Filter } from '@/scopes/search/components/QuickSearchBar/Filter'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'

const StackedWrapper = styled.div`
  display: grid;
  width: 100%;

  > * {
    grid-area: 1 / 1;
  }

  > [data-inactive='true'] {
    height: 0;
    overflow: hidden;
    visibility: hidden;
  }
`

function DepartureDateFilter({
  name,
  options,
  className,
}: {
  name: string
  options: DepartureDateFilterOptions
  className?: string
}) {
  const { view } = useDepartureDateFilter({ options })
  const isCalendarView = view === 'calendar'

  return (
    <StackedWrapper className={className}>
      <div data-inactive={!isCalendarView}>
        <DatesCalendar options={options} />
      </div>
      <div data-inactive={isCalendarView}>
        <MonthCalendar name={name} options={options} />
      </div>
    </StackedWrapper>
  )
}

const MobileDepartureDateFilter = styled(DepartureDateFilter)`
  @media ${mqMin('primitives_breakpoint_m_min')} {
    margin-bottom: 0;
    margin-top: 0;
    width: auto;
  }
`

const DesktopDepartureDateFilter = styled(DepartureDateFilter)``

const FilterWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${space('primitives_space_16')};

  ${DesktopDepartureDateFilter} {
    display: none;
  }

  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${DesktopDepartureDateFilter} {
      display: grid;

      .react-datepicker {
        gap: ${space('primitives_space_48')};
      }
    }

    ${MobileDepartureDateFilter} {
      display: none;
    }
  }
`

const FilterPopoverContent = styled(Filter)`
  @media (min-width: ${tokPx('primitives_breakpoint_s_max')}) {
    width: 100% !important; // Overriding Popover.StyledPopoverWrapper rules
  }
`

const PopoverHeader = styled(Tabs)`
  padding: ${space('primitives_space_16')};
  background: ${tok('semantics_views_background_color')};
  border-bottom: ${tokPx('semantics_dividers_tickness')} solid ${tok('semantics_dividers_color')};
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 99;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    position: initial;
    top: initial;
    width: ${tokPx('primitives_area_size_360')};
    background: initial;
    z-index: initial;
    border-bottom: initial;
  }
`

const FooterButtonWrapper = styled.div`
  display: flex;
  gap: ${space('primitives_space_8')};
  margin-left: auto;
  background-color: ${tok('semantics_views_background_color')};
  border-top: ${tokPx('semantics_dividers_tickness')} solid ${tok('semantics_dividers_color')};
  width: 100%;

  button {
    width: 100%;

    &:enabled {
      span {
        margin-left: unset;
      }
    }
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    border-top: initial;
    width: initial;
  }
`

const Footer = styled.div`
  bottom: 0;
  display: flex;
  flex-direction: column;
  position: -webkit-sticky;
  position: sticky;
  width: 100%;
  z-index: ${zIndex.level2};

  ${Toggle}, ${FooterButtonWrapper} {
    padding: ${space('primitives_space_16')};
  }

  ${Toggle} {
    flex-wrap: wrap;
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    flex-direction: row;
    background-color: ${tok('semantics_views_background_color')};
    border-top: ${tokPx('semantics_dividers_tickness')} solid ${tok('semantics_dividers_color')};
  }
`

export function DepartureDateFilterComponent({
  disabled,
  onConfirm,
  onReset,
  options,
  id,
  asSheet,
  biggerBreakpoint,
}: {
  onConfirm: ({ departureDate }: { departureDate: DateOrRange }) => void
  onReset: () => void
  options: DepartureDateFilterOptions
  disabled?: boolean
  id: string
  asSheet?: boolean
  biggerBreakpoint?: boolean
}) {
  const intl = useIntl()
  const { setActiveItem } = useQuickSearchStore()
  const { view, departureDate, reset } = useDepartureDateStore()
  const { formattedText } = useDepartureDateFilter({ options })

  const handleConfirm = () => {
    setActiveItem(null)

    if (disabled)
      return

    if (departureDate) {
      onConfirm({ departureDate })
    }
  }

  const handleReset = () => {
    onReset()
    reset()
    setActiveItem(null)
  }

  return (
    <FilterPopoverContent
      id={id}
      placeholder={intl.formatMessage({
        id: 'quicksearch_filter_departure_date_placeholder',
      })}
      title={intl.formatMessage({
        id: 'quicksearch_filter_departure_date_title',
      })}
      onConfirm={handleConfirm}
      position="bottom-right"
      selectionFormattedText={formattedText?.long}
      compactSelectionFormattedText={formattedText?.short || formattedText?.long}
      asSheet={asSheet}
      hasPadding={false}
      biggerBreakpoint={biggerBreakpoint}
      data-qa="departure-date-filter"
    >
      <PopoverHeader options={options} />
      <FilterWrapper>
        <MobileDepartureDateFilter options={options} name="mobile-departure-filter" />
        <DesktopDepartureDateFilter options={options} name="desktop-departure-filter" />
      </FilterWrapper>
      <Footer>
        {view === 'calendar' && <Toggle options={options} />}
        <FooterButtonWrapper>
          <Button onClick={handleReset} variant="transparentPrimary" disabled={disabled}>
            {intl.formatMessage({
              id: 'quicksearch_filter_departure_date_reset',
            })}
          </Button>
          <Button onClick={handleConfirm} disabled={disabled}>
            {disabled && <IndeterminateProgressCircle />}
            {intl.formatMessage({
              id: 'quicksearch_save_button_text',
            })}
          </Button>
        </FooterButtonWrapper>
      </Footer>
    </FilterPopoverContent>
  )
}
